/*
 * File: src/components/OutstandingOrders/OutstandingOrdersTable.jsx
 * Purpose: Wraps the table structure and renders the header plus order rows.
 */

import React from 'react';
import PropTypes from 'prop-types';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';

import RepDisplayName from '../RepDisplayName.jsx';
import OutstandingOrdersHeader from './OutstandingOrdersHeader.jsx';
import OutstandingOrdersRow from './OutstandingOrdersRow.jsx';

function getShippingStatusRank(order) {
  const shipments = Array.isArray(order?.shipments) ? order.shipments : [];
  const hasTracking = shipments.some(
    (shipment) => String(shipment?.Tracking_No ?? shipment?.tracking_no ?? '').trim() !== ''
  );
  const receivedQty = Number(order?.shippingSummary?.receivedQty ?? 0);

  if (!hasTracking && receivedQty <= 0) {
    return 0;
  }

  if (order?.shippingSummary?.readyToInvoice === true) {
    return 3;
  }

  if (order?.shippingSummary?.status === 'partial' || hasTracking) {
    return 2;
  }

  return 1;
}

function getTrackingSortValue(order) {
  const shipments = Array.isArray(order?.shipments) ? order.shipments : [];
  const trackingNumbers = shipments
    .map((shipment) => String(shipment?.Tracking_No ?? shipment?.tracking_no ?? '').trim())
    .filter(Boolean)
    .sort((left, right) => left.localeCompare(right));

  return trackingNumbers[0] ?? '';
}

function OutstandingOrdersTable({ orders, selectedRep, sortBy, sortDir, onSort }) {
  const displayOrders = React.useMemo(() => {
    if (!Array.isArray(orders) || sortBy !== 'ShippingStatus') {
      return orders ?? [];
    }

    const direction = sortDir === 'asc' ? 1 : -1;
    return [...orders].sort((left, right) => {
      const rankDelta = getShippingStatusRank(left) - getShippingStatusRank(right);
      if (rankDelta !== 0) {
        return rankDelta * direction;
      }

      const trackingDelta = getTrackingSortValue(left).localeCompare(getTrackingSortValue(right));
      if (trackingDelta !== 0) {
        return trackingDelta * direction;
      }

      return String(left?.SalesOrderNo ?? '').localeCompare(String(right?.SalesOrderNo ?? ''));
    });
  }, [orders, sortBy, sortDir]);

  return (
    <TableContainer>
      <Table stickyHeader data-cy="outstanding-orders-table">
        <OutstandingOrdersHeader sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
        <TableBody>
          {displayOrders?.length ? (
            displayOrders.map((order) => (
              <OutstandingOrdersRow
                key={`${order.SalesOrderNo}-${order.OrderDate}-${order.PurchaseOrderNo}-${order.ItemCode}`}
                order={order}
              />
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={12} sx={{ textAlign: 'center', padding: '1rem' }}>
                No outstanding orders found for <RepDisplayName rep={selectedRep} />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
OutstandingOrdersTable.propTypes = {
  orders: PropTypes.array.isRequired,
  selectedRep: PropTypes.string.isRequired,
  sortBy: PropTypes.string,
  sortDir: PropTypes.oneOf(['asc', 'desc']),
  onSort: PropTypes.func,
};

OutstandingOrdersTable.defaultProps = {
  sortBy: 'OrderDate',
  sortDir: 'desc',
  onSort: () => {},
};

export default React.memo(OutstandingOrdersTable);
